import { ImageUploaderComponent, ValidImageResult } from '../../Design/image-uploader/image-uploader.component';
import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PUBLIC_MEDIA_BUCKET, supabase } from '../../core/supabase/supabase.client';
import {
  Brand,
  BrandService,
  CreateBrandPayload,
} from '../../services/brand/brand.service';
import { SnackbarService } from '../../services/design/snackbar.service';
import { ImageCompressionService } from '../../services/utils/image-compression.service';

type BrandDraftMode = 'create' | 'edit';

interface BrandDraft {
  mode: BrandDraftMode;
  id?: number;
  name: string;
  slug: string;
  description: string;
  brand_type: Brand['brand_type'];
  status: Brand['status'];
  is_featured: boolean;
  icon_file_id: number | null;
  // Local only
  icon_preview_url?: string | null;
}

@Component({
  selector: 'app-brands',
  standalone: true,
  imports: [CommonModule, FormsModule, ImageUploaderComponent],
  templateUrl: './brands.component.html',
})
export class BrandsComponent implements OnInit {
  private readonly brandService = inject(BrandService);
  private readonly snackbar = inject(SnackbarService);
  private readonly imageCompression = inject(ImageCompressionService);

  loading = signal(false);
  saving = signal(false);
  deleting = signal(false);
  error = signal<string | null>(null);

  // Filters
  searchText = signal('');
  filterType = signal<string>('');
  filterStatus = signal<string>('published');
  filterFeaturedOnly = signal<boolean>(false);
  sortBy = signal<string>('created_at');
  sortOrder = signal<'ASC' | 'DESC'>('DESC');

  brands = signal<Brand[]>([]);
  currentPage = signal(1);
  pageSize = signal(10);

  draft = signal<BrandDraft | null>(null);
  deleteDraft = signal<Brand | null>(null);

  filteredBrands = computed(() => {
    let list = this.brands();
    const q = this.searchText().trim().toLowerCase();
    const type = this.filterType();
    const status = this.filterStatus();
    const featured = this.filterFeaturedOnly();

    if (q) {
      list = list.filter(
        (b) =>
          (b.name ?? '').toLowerCase().includes(q) ||
          (b.slug ?? '').toLowerCase().includes(q)
      );
    }
    if (type) {
      list = list.filter((b) => b.brand_type === type);
    }
    if (status) {
      list = list.filter((b) => b.status === status);
    }
    if (featured) {
      list = list.filter((b) => b.is_featured);
    }

    // Since we fetch sorted from API, we don't necessarily need to sort here
    // but if user changes sortBy in UI without reloading:
    const sortField = this.sortBy() as keyof Brand;
    const order = this.sortOrder();

    return [...list].sort((a, b) => {
      const valA = a[sortField] ?? '';
      const valB = b[sortField] ?? '';
      if (valA < valB) return order === 'ASC' ? -1 : 1;
      if (valA > valB) return order === 'ASC' ? 1 : -1;
      return 0;
    });
  });

  totalPages = computed(() =>
    Math.max(1, Math.ceil(this.filteredBrands().length / this.pageSize()))
  );

  paginatedBrands = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize();
    return this.filteredBrands().slice(start, start + this.pageSize());
  });

  pageNumbers = computed(() => {
    const total = this.totalPages();
    const start = Math.max(1, Math.min(this.currentPage() - 2, total - 4));
    const end = Math.min(total, start + 4);
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  });

  firstVisibleBrand = computed(() =>
    this.filteredBrands().length === 0
      ? 0
      : (this.currentPage() - 1) * this.pageSize() + 1
  );

  lastVisibleBrand = computed(() =>
    Math.min(this.currentPage() * this.pageSize(), this.filteredBrands().length)
  );

  ngOnInit(): void {
    this.loadBrands();
  }

  loadBrands(): void {
    this.loading.set(true);
    this.error.set(null);
    this.brandService
      .listBrands({
        search: this.searchText(),
        type: this.filterType(),
        status: this.filterStatus(),
        featured: this.filterFeaturedOnly() ? true : undefined,
        sortBy: this.sortBy(),
        order: this.sortOrder(),
      })
      .subscribe({
        next: (brands) => {
          this.brands.set(brands);
          if (this.currentPage() > this.totalPages()) {
            this.currentPage.set(this.totalPages());
          }
          this.loading.set(false);
        },
        error: (err) => {
          console.error('Failed to load brands', err);
          this.error.set('Failed to load brands');
          this.loading.set(false);
        },
      });
  }

  resetPageAndLoad(): void {
    this.currentPage.set(1);
    this.loadBrands();
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
    }
  }

  setPageSize(value: unknown): void {
    const size = Number(value);
    if (![10, 25, 50].includes(size)) return;
    this.pageSize.set(size);
    this.currentPage.set(1);
  }

  openCreate(): void {
    this.draft.set({
      mode: 'create',
      name: '',
      slug: '',
      description: '',
      brand_type: 'national',
      status: 'draft',
      is_featured: false,
      icon_file_id: null,
    });
  }

  openEdit(brand: Brand): void {
    this.draft.set({
      mode: 'edit',
      id: brand.id,
      name: brand.name ?? '',
      slug: brand.slug ?? '',
      description: brand.description ?? '',
      brand_type: brand.brand_type ?? 'national',
      status: brand.status,
      is_featured: !!brand.is_featured,
      icon_file_id: brand.icon_file_id ?? null,
      icon_preview_url: brand.icon?.url,
    });
  }

  closeDraft(): void {
    if (this.saving()) return;
    this.draft.set(null);
  }

  onNameChange(name: string): void {
    const d = this.draft();
    if (!d || this.saving()) return;
    this.draft.set({
      ...d,
      name,
      slug: d.mode === 'create' ? this.slugify(name) : d.slug,
    });
  }

  
  onFilesValidResult(files: ValidImageResult[]): void {
    if (!files || files.length === 0) return;
    const file = files[0].file;
    const url = files[0].url;

    this.draft.update((d) =>
      d ? { ...d, icon_preview_url: url } : null
    );
    (this as any).pendingIconFile = file;
  }

  removeIcon(): void {
    this.draft.update((d) =>
      d ? { ...d, icon_preview_url: null, icon_file_id: null } : null
    );
    (this as any).pendingIconFile = null;
  }


  async saveDraft(): Promise<void> {
    const d = this.draft();
    if (!d) return;

    if (!d.name.trim()) {
      this.snackbar.error('Brand name is required');
      return;
    }

    this.saving.set(true);

    try {
      let iconFileId = d.icon_file_id;

      // Handle file upload if pending
      let pendingFile = (this as any).pendingIconFile;
      if (pendingFile) {
        // Compress image before upload
        pendingFile = await this.imageCompression.compress(pendingFile);

        const fileName = pendingFile.name;
        const filePath = `brands/${Date.now()}-${fileName}`;
        const { error: uploadError } = await supabase.storage
          .from(PUBLIC_MEDIA_BUCKET)
          .upload(filePath, pendingFile, { upsert: true });

        if (uploadError) throw uploadError;

        const { data: fileRow, error: fileError } = await supabase
          .from('files')
          .insert({
            file_name: fileName,
            bucket: PUBLIC_MEDIA_BUCKET,
            path: filePath,
            mime_type: pendingFile.type,
            size: pendingFile.size,
            entity_type: 'brand',
            purpose: 'logo',
          })
          .select()
          .single();

        if (fileError) throw fileError;
        iconFileId = fileRow.id;
      }

      const payload: CreateBrandPayload = {
        name: d.name.trim(),
        slug: d.slug.trim() || this.slugify(d.name),
        description: d.description.trim(),
        brand_type: d.brand_type,
        status: d.status,
        is_featured: d.is_featured,
        icon_file_id: iconFileId,
      };

      if (d.mode === 'create') {
        this.brandService.createBrand(payload).subscribe({
          next: () => {
            this.snackbar.success('Brand created');
            this.finalizeSave();
          },
          error: (err) => this.handleSaveError(err),
        });
      } else if (d.id) {
        this.brandService.updateBrand(d.id, payload).subscribe({
          next: () => {
            this.snackbar.success('Brand updated');
            this.finalizeSave();
          },
          error: (err) => this.handleSaveError(err),
        });
      }
    } catch (err) {
      this.handleSaveError(err);
    }
  }

  private finalizeSave(): void {
    this.saving.set(false);
    this.draft.set(null);
    (this as any).pendingIconFile = null;
    this.loadBrands();
  }

  private handleSaveError(err: any): void {
    console.error('Save failed', err);
    this.snackbar.error('Failed to save brand');
    this.saving.set(false);
  }

  requestDelete(brand: Brand): void {
    this.deleteDraft.set(brand);
  }

  closeDelete(): void {
    if (this.deleting()) return;
    this.deleteDraft.set(null);
  }

  confirmDelete(): void {
    const b = this.deleteDraft();
    if (!b) return;

    this.deleting.set(true);
    this.brandService.deleteBrand(b.id).subscribe({
      next: () => {
        this.snackbar.success('Brand archived');
        this.deleting.set(false);
        this.deleteDraft.set(null);
        this.loadBrands();
      },
      error: (err) => {
        console.error('Failed to delete brand', err);
        this.snackbar.error('Failed to delete brand');
        this.deleting.set(false);
      },
    });
  }

  async quickStatusChange(
    brand: Brand,
    status: Brand['status']
  ): Promise<void> {
    this.brandService.updateStatus(brand.id, status).subscribe({
      next: () => {
        this.snackbar.success(`Brand updated to ${status}`);
        this.loadBrands();
      },
      error: () => this.snackbar.error('Failed to update status'),
    });
  }

  private slugify(input: string): string {
    return (input ?? '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 80);
  }
}
